import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { TeleconsultManagementCalendarDay } from '../../types/api';
import { getTodayISO } from '../../utils/date';
import {
  MAX_RANGE_DAYS,
  DatePreset,
  activePreset,
  applyPresetRange,
  eachIso,
  endOfMonthIso,
  formatChip,
  inclusiveDaySpan,
  orderedRange,
  parseIso,
  startOfMonthIso,
  toIso,
} from './managementRange';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

type Props = {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
};

type YearMonth = { y: number; m: number };

function shiftMonth(year: number, monthIndex: number, delta: number): YearMonth {
  const dt = new Date(year, monthIndex + delta, 1);
  return { y: dt.getFullYear(), m: dt.getMonth() };
}

function formatMonthTitle(year: number, monthIndex: number): string {
  return new Date(year, monthIndex, 1).toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
}

function monthCells(year: number, monthIndex: number): (string | null)[] {
  const first = new Date(year, monthIndex, 1);
  const mondayFirst = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < mondayFirst; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(toIso(year, monthIndex, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatMoney(n: number): string {
  if (n >= 100) return `$${Math.round(n)}`;
  if (n >= 10) return `$${n.toFixed(0)}`;
  return `$${n.toFixed(0)}`;
}

function formatTrackValue(day: TeleconsultManagementCalendarDay | undefined): string {
  if (!day || (day.visits <= 0 && day.stripeSgd <= 0)) return '';
  if (day.stripeSgd > 0) return formatMoney(day.stripeSgd);
  return String(day.visits);
}

export default function ManagementDateRangePicker({ from, to, onChange }: Props) {
  const today = getTodayISO();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [leftMonth, setLeftMonth] = useState<YearMonth>(() => {
    const parsed = parseIso(from);
    return { y: parsed.y, m: parsed.m };
  });
  const [daysByIso, setDaysByIso] = useState<Record<string, TeleconsultManagementCalendarDay>>({});

  const rightMonth = shiftMonth(leftMonth.y, leftMonth.m, 1);
  const currentMonth = parseIso(today);
  const selectedPreset = activePreset(from, to, today);

  const preview = useMemo(() => {
    if (draftFrom && draftTo) return orderedRange(draftFrom, draftTo);
    if (draftFrom && hoverDate) return orderedRange(draftFrom, hoverDate);
    if (draftFrom) return { from: draftFrom, to: draftFrom };
    return { from, to };
  }, [draftFrom, draftTo, hoverDate, from, to]);

  const trackDays = useMemo(() => {
    const start = startOfMonthIso(leftMonth.y, leftMonth.m);
    const end = endOfMonthIso(rightMonth.y, rightMonth.m);
    return eachIso(start, end > today ? today : end);
  }, [leftMonth.y, leftMonth.m, rightMonth.y, rightMonth.m, today]);

  const maxStripe = useMemo(
    () => Math.max(0, ...trackDays.map((iso) => daysByIso[iso]?.stripeSgd ?? 0)),
    [trackDays, daysByIso],
  );
  const maxVisits = useMemo(
    () => Math.max(1, ...trackDays.map((iso) => daysByIso[iso]?.visits ?? 0)),
    [trackDays, daysByIso],
  );
  const useStripeScale = maxStripe > 0;

  const rangeLabel = `${inclusiveDaySpan(preview.from, preview.to)} day${
    inclusiveDaySpan(preview.from, preview.to) === 1 ? '' : 's'
  }`;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        commitAndClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftFrom, draftTo]);

  useEffect(() => {
    if (!open) return undefined;
    const start = startOfMonthIso(leftMonth.y, leftMonth.m);
    const endRaw = endOfMonthIso(rightMonth.y, rightMonth.m);
    const end = endRaw > today ? today : endRaw;
    let cancelled = false;
    void apiService
      .getTeleconsultManagementCalendar({ from: start, to: end })
      .then((rows) => {
        if (cancelled) return;
        const next: Record<string, TeleconsultManagementCalendarDay> = {};
        rows.forEach((row) => {
          next[String(row.date).slice(0, 10)] = row;
        });
        setDaysByIso(next);
      })
      .catch(() => {
        if (!cancelled) setDaysByIso({});
      });
    return () => {
      cancelled = true;
    };
  }, [open, leftMonth.y, leftMonth.m, rightMonth.y, rightMonth.m, today]);

  const openPicker = () => {
    setDraftFrom(from);
    setDraftTo(to);
    const parsed = parseIso(from);
    setLeftMonth({ y: parsed.y, m: parsed.m });
    setHoverDate(null);
    setOpen(true);
  };

  const commitAndClose = (nextFrom = draftFrom, nextTo = draftTo || draftFrom) => {
    const range = orderedRange(nextFrom, nextTo || nextFrom);
    if (inclusiveDaySpan(range.from, range.to) > MAX_RANGE_DAYS) {
      setOpen(false);
      return;
    }
    if (range.from !== from || range.to !== to) {
      onChange(range.from, range.to);
    }
    setOpen(false);
  };

  const applyPreset = (preset: DatePreset) => {
    const { from: nextFrom, to: nextTo } = applyPresetRange(preset, today);
    setDraftFrom(nextFrom);
    setDraftTo(nextTo);
    const parsed = parseIso(nextFrom);
    setLeftMonth({ y: parsed.y, m: parsed.m });
    onChange(nextFrom, nextTo);
  };

  const onDayClick = (iso: string) => {
    if (iso > today) return;
    if (!draftFrom || draftTo) {
      setDraftFrom(iso);
      setDraftTo('');
      setHoverDate(null);
      return;
    }
    const range = orderedRange(draftFrom, iso);
    if (inclusiveDaySpan(range.from, range.to) > MAX_RANGE_DAYS) return;
    setDraftTo(range.to);
    setDraftFrom(range.from);
    onChange(range.from, range.to);
    setOpen(false);
  };

  const isDisabled = (iso: string) => {
    if (iso > today) return true;
    if (draftFrom && !draftTo && inclusiveDaySpan(draftFrom, iso) > MAX_RANGE_DAYS) return true;
    return false;
  };

  const metricTone = (iso: string): string => {
    const day = daysByIso[iso];
    if (!day || (day.visits <= 0 && day.stripeSgd <= 0)) return '';
    if (day.stripeSgd <= 0) return 'mgmt-cal__metric--muted';
    if (maxStripe <= 0) return 'mgmt-cal__metric--mid';
    const t = day.stripeSgd / maxStripe;
    if (t >= 0.66) return 'mgmt-cal__metric--high';
    if (t >= 0.33) return 'mgmt-cal__metric--mid';
    return 'mgmt-cal__metric--low';
  };

  const dayClass = (iso: string) => {
    const classes = ['mgmt-cal__day'];
    if (iso === today) classes.push('mgmt-cal__day--today');
    if (iso === preview.from || iso === preview.to) classes.push('mgmt-cal__day--end');
    else if (iso > preview.from && iso < preview.to) classes.push('mgmt-cal__day--in');
    if (iso === preview.from && iso !== preview.to) classes.push('mgmt-cal__day--start');
    if (iso === preview.to && iso !== preview.from) classes.push('mgmt-cal__day--stop');
    if (isDisabled(iso)) classes.push('mgmt-cal__day--disabled');
    return classes.join(' ');
  };

  const canGoNext =
    rightMonth.y < currentMonth.y || (rightMonth.y === currentMonth.y && rightMonth.m < currentMonth.m);

  const renderMonth = (year: number, monthIndex: number) => (
    <div className="mgmt-cal__month" key={`${year}-${monthIndex}`}>
      <h3 className="mgmt-cal__month-title">{formatMonthTitle(year, monthIndex)}</h3>
      <div className="mgmt-cal__weekdays">
        {WEEKDAYS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="mgmt-cal__grid">
        {monthCells(year, monthIndex).map((iso, idx) =>
          iso ? (
            <button
              key={iso}
              type="button"
              className={dayClass(iso)}
              disabled={isDisabled(iso)}
              onMouseEnter={() => setHoverDate(iso)}
              onMouseLeave={() => setHoverDate(null)}
              onClick={() => onDayClick(iso)}
            >
              <span className="mgmt-cal__num">{parseIso(iso).d}</span>
              <span className={`mgmt-cal__metric ${metricTone(iso)}`}>{formatTrackValue(daysByIso[iso])}</span>
              {daysByIso[iso] && (daysByIso[iso].visits > 0 || daysByIso[iso].stripeSgd > 0) ? (
                <span className="mgmt-cal__tip">
                  {formatChip(iso)}
                  <strong>
                    {daysByIso[iso].visits} visit{daysByIso[iso].visits === 1 ? '' : 's'}
                    {daysByIso[iso].stripeSgd > 0
                      ? ` · SGD ${daysByIso[iso].stripeSgd.toLocaleString('en-SG', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}`
                      : ''}
                  </strong>
                </span>
              ) : null}
            </button>
          ) : (
            <span key={`e-${idx}`} className="mgmt-cal__day mgmt-cal__day--empty" />
          ),
        )}
      </div>
    </div>
  );

  const presets: { id: DatePreset; label: string }[] = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: '7d', label: 'Last 7 days' },
    { id: 'month', label: 'This month' },
    { id: 'year', label: 'This year' },
  ];

  return (
    <div className="mgmt-cal" ref={wrapRef}>
      <button
        type="button"
        className={`mgmt-cal__field ${open ? 'mgmt-cal__field--open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? commitAndClose() : openPicker())}
      >
        <span className="mgmt-cal__field-side">
          <span className="mgmt-cal__field-label">From</span>
          <span className="mgmt-cal__field-value">{formatChip(from)}</span>
        </span>
        <span className="mgmt-cal__field-dash" aria-hidden="true">
          —
        </span>
        <span className="mgmt-cal__field-side">
          <span className="mgmt-cal__field-label">To</span>
          <span className="mgmt-cal__field-value">{formatChip(to)}</span>
        </span>
      </button>

      {open ? (
        <div className="mgmt-cal__popover" role="dialog" aria-label="Choose date range">
          <aside className="mgmt-cal__presets" aria-label="Date presets">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`mgmt-cal__preset${selectedPreset === p.id ? ' mgmt-cal__preset--active' : ''}`}
                onClick={() => applyPreset(p.id)}
              >
                {p.label}
              </button>
            ))}
            <span className={`mgmt-cal__preset mgmt-cal__preset--custom${selectedPreset ? '' : ' mgmt-cal__preset--active'}`}>
              Custom
            </span>
          </aside>

          <div className="mgmt-cal__main">
            <div className="mgmt-cal__toolbar">
              <button
                type="button"
                className="mgmt-cal__nav"
                aria-label="Previous month"
                onClick={() => setLeftMonth((prev) => shiftMonth(prev.y, prev.m, -1))}
              >
                ‹
              </button>
              <p className="mgmt-cal__hint">
                {draftTo || (draftFrom && hoverDate)
                  ? `${formatChip(preview.from)} – ${formatChip(preview.to)} · ${rangeLabel}`
                  : 'Select a start date, then an end date'}
              </p>
              <button
                type="button"
                className="mgmt-cal__nav"
                aria-label="Next month"
                disabled={!canGoNext}
                onClick={() => setLeftMonth((prev) => shiftMonth(prev.y, prev.m, 1))}
              >
                ›
              </button>
            </div>

            <p className="mgmt-cal__graph-title">Price graph</p>
            <div className="mgmt-cal__track" aria-hidden="true">
              {trackDays.map((iso) => {
                const visits = daysByIso[iso]?.visits ?? 0;
                const stripe = daysByIso[iso]?.stripeSgd ?? 0;
                const value = useStripeScale ? stripe : visits;
                const max = useStripeScale ? Math.max(1, maxStripe) : maxVisits;
                const inRange = iso >= preview.from && iso <= preview.to;
                const height = value <= 0 ? 6 : Math.max(10, Math.round((value / max) * 52));
                return (
                  <button
                    key={iso}
                    type="button"
                    className={`mgmt-cal__bar ${inRange ? 'mgmt-cal__bar--in' : ''}`}
                    style={{ height }}
                    title={`${formatChip(iso)} · ${visits} visit${visits === 1 ? '' : 's'}${
                      stripe ? ` · $${stripe.toFixed(0)}` : ''
                    }`}
                    onClick={() => onDayClick(iso)}
                  />
                );
              })}
            </div>
            <p className="mgmt-cal__track-caption">
              {useStripeScale ? 'Daily Stripe $ across the two months' : 'Daily visit counts across the two months'}
            </p>

            <div className="mgmt-cal__months">
              {renderMonth(leftMonth.y, leftMonth.m)}
              {renderMonth(rightMonth.y, rightMonth.m)}
            </div>

            <div className="mgmt-cal__footer">
              <div className="mgmt-cal__legend" aria-hidden="true">
                <span className="mgmt-cal__legend-item mgmt-cal__legend-item--low">Lower $</span>
                <span className="mgmt-cal__legend-item mgmt-cal__legend-item--mid">Typical</span>
                <span className="mgmt-cal__legend-item mgmt-cal__legend-item--high">Higher $</span>
              </div>
              <span className="mgmt-cal__max">Max {MAX_RANGE_DAYS} days</span>
              <button type="button" className="mgmt-cal__done" onClick={() => commitAndClose()}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
